import { exportBatchClaims, getBatchByBatchId } from 'features/autocheck/autoCheckApi'
import { BatchType, IdownloadCSV, ValidationDownloadEnum, ValidationDownloadType } from 'features/autocheck/types'
import { BatchValidationErrors, downloadFile } from 'features/autocheck/utils'
import { useEffect, useState } from 'react'
import { FaDownload, FaTimesCircle } from 'react-icons/fa'
import { IoWarning } from 'react-icons/io5'
import { Button, Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'

import { LabelButton } from '../internal/LabelButton'
import { LabelDropdown } from '../internal/LabelDropdown'

interface IProps {
  activeStep: (val: number) => void
  batchErrors: BatchValidationErrors
  batchType: BatchType
  batchId: string
  startOver: () => void
}

export const ClaimFileExclusions = ({ activeStep, batchErrors, batchType, batchId, startOver }: IProps) => {
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const {
    missingRequiredFields,
    invalidDataFormat,
    identicalUniqueIds,
    totalInvalidForImport,
    totalValidClaimsForImport,
    valueExceedsLimit,
  } = batchErrors

  const isClaimImport = batchType === BatchType.Claim_Import

  const downloadExcludedClaim = (batchId: string, type: ValidationDownloadType) => {
    exportBatchClaims(batchId, type).then((res) => {
      if (res) {
        const downloadData: IdownloadCSV = {
          data: res.data,
          fileName: 'Validation_results_BatchId_' + batchId + '.csv',
          fileType: 'text/csv',
        }
        downloadFile(downloadData)
      }
    })
  }

  useEffect(() => {
    if (batchId) {
      getBatchByBatchId(batchId).then((res) => {
        if (res) {
          setTotalRecords(res.data[0].totalRecords)
        }
      })
    }
  }, [batchId])

  return (
    <Div>
      <div className="header-container">
        <h2>
          {totalValidClaimsForImport !== 0 ? (
            <IoWarning className="warning-icon" />
          ) : (
            <FaTimesCircle className="times-circle-icon" />
          )}
          <span>{getInValidCountText(batchType, totalInvalidForImport, totalRecords)}</span>
        </h2>
        {!isClaimImport && (
          <p>
            {totalValidClaimsForImport !== 0
              ? 'These will be excluded from the batch if you proceed to upload.'
              : 'This claim file cannot be uploaded. Please review results below and try again.'}{' '}
          </p>
        )}
      </div>

      <div className="label-holder">
        <p className="title-header">Errors found in your file:</p>
        <div className="label-container">
          <Tooltip
            className="text-with-tooltip"
            tooltipText={`One or more unique IDs appear multiple times within this file. No rows with these IDs will be uploaded.`}
            position={PositionEnum.top}
            positionContent={50}
            positionTooltipArrow={10}
            tooltipStyles={{
              'max-width': '20rem',
              width: '20rem',
              zIndex: 101,
              transform: ' translateX(-53%)',
            }}
          >
            <LabelButton
              type="danger"
              content={
                <div className="label-inner">
                  <p>{identicalUniqueIds.value ?? 0} </p> {identicalUniqueIds.label}
                </div>
              }
            />
          </Tooltip>

          <LabelDropdown
            tooltipText={
              <div>
                <p>Rows had one or more issues:</p>
                <ul>
                  <li>missing required fields</li>
                  <li>invalid data types (e.g. numbers in a “Name” field, where only letters are allowed)</li>
                  <li>invalid format or length (e.g. too many digits for NPI)</li>
                  <li>dollar amount exceeds $100M</li>
                </ul>
              </div>
            }
            title={
              <div className="label-inner">
                <p>{calculateDataValidation(batchErrors)} </p> Data validation failure(s)
              </div>
            }
            items={[
              {
                text: (
                  <>
                    <b>{missingRequiredFields.value ?? 0} </b> {missingRequiredFields.label}
                  </>
                ),
              },
              {
                text: (
                  <>
                    <b>{invalidDataFormat.value ?? 0} </b> {invalidDataFormat.label}
                  </>
                ),
              },
              {
                text: (
                  <>
                    <b>{valueExceedsLimit.value ?? 0}</b> {valueExceedsLimit.label}
                  </>
                ),
              },
            ]}
          />
          {/* </CustomTooltip> */}
        </div>
        <div className="download-btn-container">
          <p>Single rows may include multiple errors. Details are available in the validation report below.</p>
          <Button
            variant="secondary"
            size="small"
            radius="normal"
            onClick={() => downloadExcludedClaim(batchId, ValidationDownloadEnum.errorOnly)}
          >
            <FaDownload className="download-icon" /> Download Excluded Claims Report
          </Button>
        </div>
      </div>

      <div className="main-btn-container">
        {totalValidClaimsForImport !== 0 ? (
          <Button
            data-testid="file-exclusion-btn"
            variant="info"
            radius="rounded"
            className="btn-exclusion"
            size="small"
            color="info"
            onClick={() => activeStep(3)}
          >
            Proceed without these claims
          </Button>
        ) : (
          <Button
            data-testid="file-upload-btn"
            variant="info-outline"
            radius="rounded"
            className=" btn-start-over"
            size="small"
            color="info"
            onClick={() => startOver()}
          >
            Start Over
          </Button>
        )}
      </div>
    </Div>
  )
}

const Div = styled.div`
  .header-container {
    text-align: center;
    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.neutral.neutral};
      margin-bottom: 0;
      .times-circle-icon {
        color: ${({ theme }) => theme.colors.error.neutral};
        margin-bottom: 0.25rem;
        margin-right: 0.5rem;
      }
      .warning-icon {
        margin-bottom: 0.25rem;
        margin-right: 0.5rem;
        color: ${({ theme }) => theme.colors.warning.neutral};
      }
    }
    p {
      font-size: 1rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.grey.dark};
    }
  }
  .label-holder {
    padding: 1.25rem 0.75rem;
    margin: ${({ theme }) => theme.spacing.l} ${({ theme }) => theme.spacing.xxxl};
    border-radius: 8px;
    border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
    .title-header {
      ${({ theme }) => theme.typography.h6.normal};
      color: ${({ theme }) => theme.colors.neutral.neutral};
      margin-bottom: ${({ theme }) => theme.spacing.s};
      text-align: center;
    }
  }
  .label-container {
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;

    div,
    button {
      width: 100%;
    }
    .label-inner {
      display: flex;
      align-items: center;
    }
    p {
      font-size: 1rem;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      margin-right: 0.3rem;
    }
  }
  .download-btn-container {
    margin-top: ${({ theme }) => theme.spacing.s};
    display: flex;
    align-items: center;

    flex-direction: column;
    p {
      font-size: 0.875rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.14;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.grey.dark};
      margin-bottom: 1rem;
    }
    button,
    .download-icon {
      font-size: 1rem;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.primary.neutral};
    }
  }
  .main-btn-container {
    margin: 2.25rem;
    button {
      height: 3rem;
      width: 100%;
      font-size: 1.125rem;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;
      text-align: center;
    }
  }
  .text-with-tooltip .tooltip-container p {
    font-size: 0.75rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }
  .text-with-tooltip ul {
    margin-left: 1.25rem;
  }
  .text-with-tooltip ul li {
    list-style: disc;
    text-align: left;
  }
`

function getInValidCountText(type: BatchType, count: number | undefined, totalRecords: number | undefined) {
  if (type === BatchType.Claim_Autocheck || type === BatchType.Claim_Autocheck_EOB || type === BatchType.Claim_ASC) {
    return `${count ?? 0} of ${totalRecords ?? 0} row(s) ineligible for upload`
  } else if (type === BatchType.Claim_Import) {
    return `${count ?? 0} of ${totalRecords ?? 0} row(s) ineligible for upload`
  }
}

function calculateDataValidation(batchErrors: BatchValidationErrors) {
  const { missingRequiredFields, invalidDataFormat, valueExceedsLimit } = batchErrors
  let total = 0
  if (missingRequiredFields.value) {
    total = missingRequiredFields.value
  }
  if (invalidDataFormat.value) {
    total += invalidDataFormat.value
  }
  if (valueExceedsLimit.value) {
    total += valueExceedsLimit.value
  }
  return total
}
